import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  LayoutDashboard,
  MessageCircle,
  BookOpen,
  Mic,
  LogOut,
  ChevronRight,
  Building2,
  Sparkles,
  Menu,
  Settings,
  PanelRightOpen,
  Flame,
  Crown,
  DollarSign,
  Spade,
  ShoppingBag,
  Trophy,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { ProfileSettings } from "@/components/ProfileSettings"
import { RedeemModal } from "@/components/RedeemModal"
import { RightSidebar } from "@/components/RightSidebar"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import ovalMoneyClubLogo from "@/public/oval-money-club-logo.png"
import ovalVideo from "@/public/oval.mp4"
import coinImg from "@/public/coin.png"
import ovalPillImg from "@/public/oval-pill.png"
import moneyImg from "@/public/money.png"

export type TabType =
  | "dashboard"
  | "chat"
  | "resources"
  | "poker"
  | "store"
  | "events"
  | "leaderboard"

interface AppSidebarProps {
  activeTab: TabType
  onTabChange: (tab: TabType) => void
  children: React.ReactNode
}

const navItems = [
  {
    id: "dashboard" as const,
    label: "Dashboard",
    icon: LayoutDashboard,
    color: "#F37020",
    activeGradient: "from-[#F37020] to-[#FF8C42]",
  },
  {
    id: "leaderboard" as const,
    label: "Leaderboard",
    icon: Trophy,
    color: "#FFD700",
    activeGradient: "from-[#FFD700] to-[#FFA500]",
  },
  {
    id: "chat" as const,
    label: "Agency Chat",
    icon: MessageCircle,
    color: "#00CED1",
    activeGradient: "from-[#00CED1] to-[#48D1CC]",
  },
  {
    id: "events" as const,
    label: "Events",
    icon: Mic,
    color: "#F59E0B",
    activeGradient: "from-[#F59E0B] to-[#FBBF24]",
  },
  {
    id: "store" as const,
    label: "Store",
    icon: ShoppingBag,
    color: "#0A5BFF",
    activeGradient: "from-[#0A5BFF] to-[#6FA3FF]",
  },
  {
    id: "poker" as const,
    label: "Poker Room",
    icon: Spade,
    color: "#32CD32",
    activeGradient: "from-[#32CD32] to-[#3ddc84]",
  },
  {
    id: "resources" as const,
    label: "Resources",
    icon: BookOpen,
    color: "#3B82F6",
    activeGradient: "from-[#3B82F6] to-[#60A5FA]",
  },
]

function RetroNavItem({
  item,
  isActive,
  onClick,
  index,
}: {
  item: (typeof navItems)[number]
  isActive: boolean
  onClick: () => void
  index: number
}) {
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={onClick}
        isActive={isActive}
        tooltip={item.label}
        className={`
          relative group/nav-item h-[52px] transition-all duration-200
          ${isActive
            ? "bg-[#1C1C32] border-[2.5px] border-[#2A2A45] shadow-[0_0_10px_rgba(243,112,32,0.15)]"
            : "bg-[#1C1C32]/60 border-[2.5px] border-[#2A2A45]/60 hover:bg-[#222240] hover:border-[#2A2A45] hover:shadow-[0_0_6px_rgba(243,112,32,0.1)]"
          }
          rounded-2xl overflow-hidden
        `}
      >
        {/* Active indicator bar */}
        {isActive && (
          <motion.div
            className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${item.activeGradient} rounded-l-2xl`}
            layoutId="activeNavIndicator"
            transition={{ type: "spring", bounce: 0.25, duration: 0.4 }}
          />
        )}

        <motion.div
          className="flex items-center gap-3 w-full"
          whileHover={{ x: isCollapsed ? 0 : 2 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
        >
          <motion.div
            className={`
              w-9 h-9 rounded-xl flex items-center justify-center border-2
              ${isActive ? "border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)]" : "border-[#2A2A45]/30 shadow-none"}
            `}
            style={{
              background: isActive
                ? `linear-gradient(135deg, ${item.color}, ${item.color}dd)`
                : `${item.color}25`,
            }}
            whileHover={{ scale: 1.08, rotate: isActive ? 0 : 5 }}
          >
            <item.icon
              className={`w-[18px] h-[18px] ${isActive ? "text-white" : "text-[#9A9ABF]"}`}
            />
          </motion.div>
          {!isCollapsed && (
            <>
              <span
                className={`font-bold text-[13px] flex-1 ${
                  isActive ? "text-white" : "text-[#9A9ABF]"
                }`}
                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
              >
                {item.label}
              </span>
              {isActive && (
                <motion.div
                  initial={{ scale: 0, x: 10 }}
                  animate={{ scale: 1, x: 0 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                >
                  <ChevronRight className="w-4 h-4 text-[#6A6A8A]" />
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function SidebarContentInner({
  activeTab,
  onTabChange,
}: {
  activeTab: TabType
  onTabChange: (tab: TabType) => void
}) {
  const { user, logout } = useAuth()
  const { state } = useSidebar()
  const isCollapsed = state === "collapsed"
  const [videoLoaded, setVideoLoaded] = React.useState(false)
  const [profileOpen, setProfileOpen] = React.useState(false)
  const [redeemOpen, setRedeemOpen] = React.useState(false)

  const displayName =
    user?.onboardingData?.displayName ||
    user?.onboardingData?.agencyName ||
    user?.email?.split("@")[0] ||
    "Champion"

  const userType = user?.onboardingData?.userType
  const isAgent = userType === "agent"
  const agencyId = user?.onboardingData?.agencyId
  const agencyName = isAgent ? user?.onboardingData?.agencyName : null

  // Points & balance info for the sidebar
  const weeklyDeals = user?.points?.weeklyDeals ?? 0
  const currentPoints = user?.points?.current ?? 0
  const bonusEarned = user?.balance?.available ?? 0

  return (
    <>
      {/* Header with Logo - Arcade Cabinet Style */}
      <SidebarHeader className="p-0 overflow-hidden">
        {!isCollapsed ? (
          <motion.div
            className="relative cursor-pointer"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", bounce: 0.3 }}
            onClick={() => onTabChange("dashboard")}
            role="button"
            aria-label="Go to dashboard"
          >
            {/* Outer frame - arcade cabinet style */}
            <div className="relative bg-gradient-to-b from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] p-1.5">
              {/* Inner gold trim */}
              <div className="relative bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FF6B00] p-0.5 rounded-sm">
                {/* Screen bezel effect */}
                <div className="relative overflow-hidden rounded-sm bg-black">
                  {/* Scanline overlay for retro CRT effect */}
                  <div
                    className="absolute inset-0 z-20 pointer-events-none opacity-[0.03]"
                    style={{
                      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
                    }}
                  />

                  {/* Vignette effect */}
                  <div
                    className="absolute inset-0 z-10 pointer-events-none"
                    style={{
                      boxShadow: 'inset 0 0 60px rgba(0,0,0,0.4)',
                    }}
                  />

                  {/* Loading fallback - bouncing coins */}
                  {!videoLoaded && (
                    <div className="flex items-center justify-center py-10 bg-gradient-to-br from-[#161628] to-[#1C1C32]">
                      <div className="flex gap-2">
                        {[0, 1, 2].map((i) => (
                          <motion.img
                            key={i}
                            src={coinImg}
                            alt=""
                            className="w-8 h-8 drop-shadow-lg"
                            animate={{
                              y: [0, -15, 0],
                              rotate: [0, 180, 360],
                            }}
                            transition={{
                              duration: 0.8,
                              repeat: Infinity,
                              delay: i * 0.15,
                              ease: "easeInOut"
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Main video */}
                  <motion.video
                    src={ovalVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={`w-full h-auto relative z-0 ${videoLoaded ? "block" : "hidden"}`}
                    onLoadedData={() => setVideoLoaded(true)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: videoLoaded ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Shimmer overlay on load */}
                  {videoLoaded && (
                    <motion.div
                      className="absolute inset-0 z-10 pointer-events-none"
                      initial={{ x: "-100%", opacity: 0.6 }}
                      animate={{ x: "200%", opacity: 0 }}
                      transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
                      style={{
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                        width: "50%",
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Corner bolts/rivets for arcade feel */}
              {[
                "top-0.5 left-0.5",
                "top-0.5 right-0.5",
                "bottom-0.5 left-0.5",
                "bottom-0.5 right-0.5"
              ].map((pos, i) => (
                <div
                  key={i}
                  className={`absolute ${pos} w-2 h-2 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border border-gray-700 shadow-inner`}
                />
              ))}
            </div>

            {/* Bottom chrome strip */}
            <div className="h-1 bg-gradient-to-r from-gray-700 via-gray-500 to-gray-700" />
          </motion.div>
        ) : (
          <div
            className="p-3 bg-gradient-to-b from-[#1a1a1a] to-[#2d2d2d] cursor-pointer"
            onClick={() => onTabChange("dashboard")}
            role="button"
            aria-label="Go to dashboard"
          >
            <motion.div
              className="flex items-center justify-center p-2 bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FF6B00] rounded-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", bounce: 0.4 }}
            >
              <motion.img
                src={coinImg}
                alt=""
                className="w-7 h-7 drop-shadow-md"
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  rotate: { duration: 3, repeat: Infinity, ease: "linear" },
                  scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
                }}
              />
            </motion.div>
          </div>
        )}
      </SidebarHeader>

      {/* Earnings Indicator Widget (below header, above nav) */}
      {!isCollapsed ? (
        <div className="px-3 pt-3 pb-1 space-y-2">
          <motion.div
            className="flex rounded-xl border-[2.5px] border-[#2A2A45] shadow-[0_0_8px_rgba(243,112,32,0.12)] overflow-hidden"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            {/* Left panel — Points */}
            <div className="flex items-center gap-2 flex-1 bg-[#1C1C32] p-2.5">
              <motion.img
                src={coinImg}
                alt=""
                className="w-6 h-6"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              />
              <div className="min-w-0">
                <p
                  className="text-sm font-black text-white leading-tight"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  {currentPoints.toLocaleString()}
                </p>
                <p
                  className="text-[9px] font-bold text-[#6A6A8A] uppercase tracking-wide"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  Points available
                </p>
              </div>
            </div>
            {/* Right panel — Bonus earned / Chargeback */}
            <div
              className={`flex items-center gap-2 flex-1 p-2.5 ${
                bonusEarned >= 0
                  ? "bg-[#32CD32]"
                  : "bg-red-500"
              }`}
            >
              <DollarSign className="w-5 h-5 text-white shrink-0" />
              <div className="min-w-0">
                <p
                  className="text-sm font-black text-white leading-tight"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  ${Math.abs(bonusEarned).toLocaleString()}
                </p>
                <p
                  className="text-[9px] font-bold text-white/80 uppercase tracking-wide"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  {bonusEarned >= 0 ? "Bonus earned" : "Chargeback"}
                </p>
              </div>
            </div>
          </motion.div>
          {/* Redeem button */}
          <motion.button
            onClick={() => setRedeemOpen(true)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-[#1C1C32] border-[2.5px] border-[#2A2A45] rounded-xl shadow-[0_0_6px_rgba(243,112,32,0.1)] font-bold text-xs text-[#F37020] cursor-pointer"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            whileHover={{ scale: 1.03, boxShadow: "3px 3px 0 0 #1a1a1a" }}
            whileTap={{ scale: 0.97, boxShadow: "1px 1px 0 0 #1a1a1a", x: 1, y: 1 }}
          >
            <DollarSign className="w-3.5 h-3.5" />
            Redeem
          </motion.button>
        </div>
      ) : (
        <div className="px-2 pt-3 pb-1 flex justify-center">
          <motion.img
            src={coinImg}
            alt=""
            className="w-6 h-6"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          />
        </div>
      )}

      {/* Navigation */}
      <SidebarContent className="p-3">
        {!isCollapsed && (
          <motion.p
            className="text-[10px] font-bold text-[#6A6A8A] uppercase tracking-[0.12em] mb-2.5 px-1"
            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            Navigation
          </motion.p>
        )}
        <SidebarMenu className="space-y-2">
          {navItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.12 + index * 0.04, type: "spring", bounce: 0.25 }}
            >
              <RetroNavItem
                item={item}
                isActive={activeTab === item.id}
                onClick={() => onTabChange(item.id)}
                index={index}
              />
            </motion.div>
          ))}
        </SidebarMenu>
      </SidebarContent>

      {/* Footer with User Info and Settings */}
      <SidebarFooter className="p-3 border-t-2 border-[#2A2A45]/40">
        {/* User Info Section - Clickable */}
        <motion.button
          onClick={() => setProfileOpen(true)}
          className={`
            w-full text-left bg-gradient-to-br from-[#1C1C32] to-[#222240] rounded-2xl border-[2.5px] border-[#2A2A45] shadow-[0_0_8px_rgba(243,112,32,0.12)] mb-2.5 cursor-pointer
            hover:shadow-[0_0_12px_rgba(243,112,32,0.2)] transition-shadow
            ${isCollapsed ? "p-2" : "p-3"}
          `}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, type: "spring" }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {!isCollapsed ? (
            <div className="flex items-center gap-3">
              <div className="relative">
                <motion.div
                  className="w-11 h-11 rounded-xl overflow-hidden bg-gradient-to-br from-[#F37020] to-[#FF6B00] flex items-center justify-center border-2 border-[#2A2A45] shadow-[0_0_6px_rgba(243,112,32,0.1)]"
                >
                  {user?.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt={displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span
                      className="text-white font-black text-lg"
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </motion.div>
                {userType === "agency" && (
                  <motion.div
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#FFD700] rounded-full border-2 border-[#2A2A45] flex items-center justify-center shadow-none"
                    animate={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Crown className="w-2.5 h-2.5 text-black" />
                  </motion.div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="font-bold text-white truncate text-sm"
                  style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                >
                  {displayName}
                </p>
                <div className="flex items-center gap-1 mt-0.5">
                  {isAgent && agencyId ? (
                    <>
                      <Building2 className="w-3 h-3 text-purple-500" />
                      <span
                        className="text-[10px] font-bold text-purple-600 truncate"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {agencyName || agencyId}
                      </span>
                    </>
                  ) : userType === "agency" ? (
                    <>
                      <Building2 className="w-3 h-3 text-[#F37020]" />
                      <span
                        className="text-[10px] font-bold text-[#F37020]"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        Agency Owner
                      </span>
                    </>
                  ) : (
                    <span
                      className="text-[10px] font-medium text-[#6A6A8A]"
                      style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                    >
                      Solo Agent
                    </span>
                  )}
                </div>
              </div>
              <Settings className="w-4 h-4 text-[#6A6A8A]" />
            </div>
          ) : (
            <motion.div
              className="w-8 h-8 mx-auto rounded-lg overflow-hidden bg-gradient-to-br from-[#F37020] to-[#FF6B00] flex items-center justify-center border-2 border-[#2A2A45]"
            >
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white font-bold text-sm">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              )}
            </motion.div>
          )}
        </motion.button>

        {/* Profile Settings Sheet */}
        <ProfileSettings open={profileOpen} onOpenChange={setProfileOpen} />
        {/* Redeem Modal */}
        <RedeemModal open={redeemOpen} onOpenChange={setRedeemOpen} />
      </SidebarFooter>
    </>
  )
}

export function AppSidebar({ activeTab, onTabChange, children }: AppSidebarProps) {
  const [rightSidebarMobileOpen, setRightSidebarMobileOpen] = React.useState(false)

  return (
    <SidebarProvider defaultOpen={true}>
      <Sidebar
        collapsible="icon"
        className="border-r-3 border-[#2A2A45] bg-[#161628]"
      >
        <SidebarContentInner activeTab={activeTab} onTabChange={onTabChange} />
      </Sidebar>
      <SidebarInset className="bg-transparent overflow-hidden">
        {/* Mobile Header */}
        <header className="flex md:hidden items-center justify-between px-3 py-2.5 bg-[#161628]/95 backdrop-blur-sm border-b-3 border-[#2A2A45] sticky top-0 z-50">
          <SidebarTrigger className="border-2 border-[#2A2A45] rounded-xl p-2 bg-[#FFD700] shadow-[0_0_6px_rgba(243,112,32,0.1)] active:shadow-none active:translate-x-px active:translate-y-px transition-all">
            <Menu className="w-5 h-5" />
          </SidebarTrigger>
          <button
            type="button"
            onClick={() => onTabChange("dashboard")}
            className="flex items-center gap-2 rounded-lg px-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F37020]"
            aria-label="Go to dashboard"
          >
            <img src={ovalPillImg} alt="" className="w-7 h-7" />
            <img src={ovalMoneyClubLogo} alt="Oval Money Club" className="h-7" />
          </button>
          <button
            onClick={() => setRightSidebarMobileOpen(true)}
            className="border-2 border-[#2A2A45] rounded-xl p-2 bg-[#161628] shadow-[0_0_6px_rgba(243,112,32,0.1)] active:shadow-none active:translate-x-px active:translate-y-px transition-all"
          >
            <PanelRightOpen className="w-5 h-5" />
          </button>
        </header>
        <div className="flex flex-1 min-h-0 h-full">
          <div className="flex-1 min-w-0 overflow-y-auto scrollbar-hide touch-pan-y">
            {children}
          </div>
          <RightSidebar
            mobileOpen={rightSidebarMobileOpen}
            onMobileOpenChange={setRightSidebarMobileOpen}
            onNavigateToEvents={() => onTabChange("events")}
          />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
